import { observer } from 'mobx-react'
import { QRCodeSVG } from 'qrcode.react'
import { useContext, useEffect, useRef, useState } from 'react'
import { useInterval } from 'usehooks-ts'
import { Button, Header, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import getUserAgent from '@/methods/getUserAgent'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import { API_URL, WALLET_VERIFICATION_SERVICE_PATH } from '@/constants/apiUrl'
import { AmexGreatLayout } from './AmexGreatLayout'
import { format } from '@/methods/format'

// The web-api asks for a poll every 2-3 s while the page is visible.
const POLL_INTERVAL_MS = 2500
const COUNTDOWN_TICK_MS = 1000
const QR_CODE_SIZE = 160
// Browsers never tell a page whether an app handles a URL scheme. After handing the deep link
// over, the page waits this long for the wallet app to take over (the page gets hidden); if it
// is still visible afterwards, no wallet app opened (not installed, or the hand-over was blocked).
const APP_OPEN_DETECTION_MS = 2500

const STATUSES = ['idle', 'pending', 'expired', 'completed'] as const
type WalletVerificationStatus = (typeof STATUSES)[number]

const isKnownStatus = (status: unknown): status is WalletVerificationStatus =>
  STATUSES.includes(status as WalletVerificationStatus)

// none: no hand-over yet | pending: deep link handed over, waiting to see whether the page gets
// hidden | opened: it did | notDetected: it stayed visible
type AppOpenAttempt = 'none' | 'pending' | 'opened' | 'notDetected'

interface GetStatusResponse {
  status: WalletVerificationStatus
  // Unix timestamp in seconds, sent while `pending`
  expiresAt?: number
  // Sent when `completed`; onboarding decides what to show next
  redirectUrl?: string
}

interface StartPresentationResponse {
  sessionId: string
  // Payload of the QR code and, at the same time, the deep link into the wallet app
  qrCodeData: string
  // Unix timestamp in seconds
  expiresAt: number
}

const formatRemainingTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
}

export const WalletVerificationAmexGreatPage = observer(() => {
  const store = useContext(StoreContext)
  const { walletVerificationPage: trans } = store.TranslationsState.translations

  // Phones and tablets hand the request to the wallet app on the same device; everything else
  // shows the QR code to scan with the device that holds the wallet. Based on the user agent, not
  // on the viewport: a narrow desktop window has no wallet app to open.
  const isMobileDevice = getUserAgent().isMobile

  const [status, setStatus] = useState<WalletVerificationStatus>('idle')
  const [qrCodeData, setQrCodeData] = useState('')
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [shouldPoll, setShouldPoll] = useState(true)
  const [isStartingPresentation, setIsStartingPresentation] = useState(false)
  const [hasStartFailed, setHasStartFailed] = useState(false)
  const [appOpenAttempt, setAppOpenAttempt] = useState<AppOpenAttempt>('none')
  const [showQrCodeOnMobile, setShowQrCodeOnMobile] = useState(false)

  // Synchronous re-entrancy lock: state updates are async, so a ref guards
  // against concurrent start-presentation calls (which would create multiple requests).
  const isStartingPresentationRef = useRef(false)
  const appOpenTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const isVisible = usePageVisibility()

  const startPresentation = async (force = false): Promise<void> => {
    if (isStartingPresentationRef.current) return

    isStartingPresentationRef.current = true
    setIsStartingPresentation(true)
    setHasStartFailed(false)

    try {
      // Without `force` a presentation that is still valid is returned again (page refresh
      // keeps the QR code); with `force` a new one is created after the old one expired.
      const { data } = await axiosInstance.post<StartPresentationResponse>(
        `${API_URL}/${WALLET_VERIFICATION_SERVICE_PATH}/start-presentation`,
        force ? { force: true } : {},
        { withCredentials: true }
      )

      if (typeof data?.qrCodeData !== 'string' || !data.qrCodeData) {
        throw new Error('Unexpected start-presentation response')
      }

      setQrCodeData(data.qrCodeData)
      setExpiresAt(data.expiresAt)
      setStatus('pending')
    } catch (e) {
      devLog(e)
      setHasStartFailed(true)
    } finally {
      isStartingPresentationRef.current = false
      setIsStartingPresentation(false)
    }
  }

  const checkStatus = async (): Promise<void> => {
    try {
      const { data } = await axiosInstance.get<GetStatusResponse>(
        `${API_URL}/${WALLET_VERIFICATION_SERVICE_PATH}/get-status`,
        { withCredentials: true }
      )

      // Anything else (a proxy error page, a shape change) must not wipe the current state.
      if (!isKnownStatus(data?.status)) {
        devLog('Unexpected get-status response', data)
        return
      }

      if (data.status === 'completed') {
        setStatus('completed')
        setShouldPoll(false)
        // Onboarding shows what comes next, including a declined presentation or a failed
        // verification - the page never sees those outcomes.
        if (data.redirectUrl) forceRedirect(data.redirectUrl)
        return
      }

      setStatus(data.status)

      if (data.status === 'pending' && data.expiresAt) {
        setExpiresAt(data.expiresAt)
      }

      // Nothing requested yet, or the page was reloaded while a presentation is still valid.
      if (data.status === 'idle' || (data.status === 'pending' && !qrCodeData)) {
        await startPresentation()
      }
    } catch (e) {
      devLog(e)
    }
  }

  // Called right before the deep link is handed to the browser (auto-open or button).
  const armAppOpenDetection = (): void => {
    clearTimeout(appOpenTimerRef.current)
    setAppOpenAttempt('pending')
    appOpenTimerRef.current = setTimeout(() => {
      setAppOpenAttempt((current) => (current === 'pending' ? 'notDetected' : current))
    }, APP_OPEN_DETECTION_MS)
  }

  useEffect(() => {
    setShouldPoll(isVisible)
    // Runs on the first render as well. When the user comes back from the wallet app on the
    // same device the browser may have throttled the timers, so poll right away.
    if (isVisible) checkStatus()
  }, [isVisible])

  // The page getting hidden right after the hand-over means the wallet app took over.
  useEffect(() => {
    if (!isVisible && appOpenAttempt === 'pending') {
      clearTimeout(appOpenTimerRef.current)
      setAppOpenAttempt('opened')
    }
  }, [isVisible, appOpenAttempt])

  // Same device: hand every new presentation to the wallet app right away instead of showing a
  // QR code. Some browsers refuse a hand-over that no tap triggered, so the "Open wallet app"
  // button stays as the manual path.
  useEffect(() => {
    if (!isMobileDevice || status !== 'pending' || !qrCodeData) return

    armAppOpenDetection()
    window.location.assign(qrCodeData)
  }, [isMobileDevice, status, qrCodeData])

  useEffect(() => () => clearTimeout(appOpenTimerRef.current), [])

  useInterval(checkStatus, shouldPoll && status !== 'completed' ? POLL_INTERVAL_MS : null)

  const isExpired = status === 'expired'
  const hasPresentation = status === 'pending' && !!qrCodeData
  const showAppHandOver = hasPresentation && isMobileDevice
  const showQrCode = hasPresentation && (!isMobileDevice || showQrCodeOnMobile)
  const showRetryButton = hasStartFailed || isExpired
  const showSpinner = !hasPresentation && !showRetryButton

  useInterval(() => setNow(Date.now()), hasPresentation ? COUNTDOWN_TICK_MS : null)

  // The countdown is informative only: `get-status` decides when the request has expired.
  const remainingSeconds =
    expiresAt === null ? null : Math.max(0, expiresAt - Math.floor(now / 1000))

  const getDescription = (): string | undefined => {
    if (hasStartFailed) return trans?.startFailedDescription
    if (isExpired) return isMobileDevice ? trans?.expiredAppDescription : trans?.expiredDescription
    if (showAppHandOver) {
      return appOpenAttempt === 'notDetected'
        ? trans?.appNotDetectedDescription
        : trans?.openAppDescription
    }
    if (showQrCode) return trans?.scanQrDescription
    return trans?.preparingDescription
  }

  const getRetryLabel = (): string | undefined => {
    if (!isExpired) return trans?.tryAgainBtn
    return isMobileDevice ? trans?.startAgainBtn : trans?.requestNewQrCodeBtn
  }

  return (
    <AmexGreatLayout>
      <div className="page-container mobile-m-0">
        <Header>{trans?.header}</Header>

        <div
          className="generic__paragraph-centered m-0-0-30"
          data-testid="wallet-verification-description"
          data-app-open-attempt={showAppHandOver ? appOpenAttempt : undefined}
        >
          {getDescription()}
        </div>

        {hasPresentation && (
          <div className="wallet-verification__presentation">
            {showQrCode && (
              <QRCodeSVG
                value={qrCodeData}
                size={QR_CODE_SIZE}
                data-testid="wallet-verification-qr-code"
                role="img"
                aria-label={trans?.qrCodeAlt}
                title={trans?.qrCodeAlt}
              />
            )}

            {remainingSeconds !== null && (
              <div
                className="wallet-verification__countdown"
                data-testid="wallet-verification-countdown"
              >
                {format(trans?.expiresIn, { time: formatRemainingTime(remainingSeconds) })}
              </div>
            )}

            <div className="generic__paragraph-centered">{trans?.sharedDataDescription}</div>
          </div>
        )}

        {showSpinner && <LoadingSpinner className="loading-spinner--small-padding" />}

        {(hasPresentation || showRetryButton) && (
          <div className="layout__content-container-bottom-centered wallet-verification__actions">
            {hasPresentation && (
              // A real link, not a button with a redirect: the wallet app owns the `openid4vp://`
              // scheme, and mobile browsers hand a link navigation over to it most reliably.
              <a
                className="button onboarding-button button--proceed wallet-verification__open-app"
                href={qrCodeData}
                onClick={armAppOpenDetection}
                data-testid="wallet-verification-open-app"
              >
                <span className="generic__button-text">{trans?.openWalletAppBtn}</span>
              </a>
            )}

            {showAppHandOver && !showQrCodeOnMobile && (
              <Button
                onClick={() => setShowQrCodeOnMobile(true)}
                className="button--link"
                dataTestid="wallet-verification-show-qr"
              >
                <span className="generic__button-text">{trans?.showQrCodeBtn}</span>
              </Button>
            )}

            {showRetryButton && (
              <Button
                disabled={isStartingPresentation}
                onClick={() => startPresentation(isExpired)}
                className="button--proceed"
                dataTestid="wallet-verification-request-new"
              >
                {isStartingPresentation && <LoadingSpinner inButton />}
                <span className="generic__button-text">{getRetryLabel()}</span>
              </Button>
            )}
          </div>
        )}
      </div>
    </AmexGreatLayout>
  )
})
